/* @HEADER
 * @version 1.0.1328 | 2026-09-13
 * @purpose Keep a fresh picture of the running interface at every moment the page can be left, so an ordinary reload has something to paint instead of "Загружаем Zvenix…".
 * @sidecar shellSnapshotCapture.ts.header.md
 * @layer app | @hex adapter | @ctx pwa
 * @public true
 * @edit careful
 */

// SpecRefs: ZVX-DEV-4342, ZVX-DEV-4302, ZVX-DEV-4244, ZVX-DEV-4273, ZVX-DEV-5861

/**
 * ZVX-DEV-5861 — the cost of painting the picture on every reload, measured
 * on the production build: for ~550 ms the menu on screen is the picture,
 * and with `pointer-events: none` it swallowed the person's first click on
 * every reload. `index.html` now lets the picture take the click and replays
 * it on the live element by identity (`__zvxArmGhostReplay`). This file is
 * unchanged in behaviour; the note is here because this is where the
 * "every reload" decision lives, and the next reader should know its price.
 */

/**
 * ## Measured, and it is why the placeholder was still being seen
 *
 * ZVX-DEV-4244 made the previous interface survive an update: the running
 * screen is captured, and `index.html` paints it on the way back up. It worked —
 * and it covered exactly one route into a reload.
 *
 * `captureShellForUpdate` had a single caller: `runApply()` in
 * `UpdateAvailableNotice`, which runs only when the person CLICKS the update
 * button. Every other way of leaving a page — F5, Ctrl+R, closing and reopening
 * the tab, following a link back — wrote no snapshot at all, so there was
 * nothing to paint and the boot placeholder was what came up.
 *
 * Measured on staging, 0.16.1284 -> 0.16.1285, with the application open and a
 * plain reload: 111 samples, the ghost painted in NONE of them, and one frame
 * at 0ms showing "Загружаем Zvenix…" — captured as a screenshot, because the
 * DOM read at that same instant reported the app as present and would have
 * passed the run.
 *
 * The uncovered route is the common one. People reload and return to tabs far
 * more often than they click an update button.
 *
 * ## Why these two events
 *
 * `pagehide` is the last reliable moment before a page goes away, and it fires
 * on reload — which is the case that was uncovered. `visibilitychange` to
 * hidden covers mobile, where a tab can be discarded without `pagehide` ever
 * running.
 *
 * `beforeunload` is deliberately NOT used: registering it can disable the
 * back/forward cache in some browsers, which would make ordinary navigation
 * slower for everyone in order to protect one moment.
 *
 * ## Why a minimum interval rather than a timer
 *
 * Serializing the rendered screen costs real work, and these events can fire in
 * bursts (a tab hidden and shown repeatedly). A short floor between captures
 * keeps that bounded. There is no periodic timer: a snapshot is only ever
 * needed at the moment of leaving, so taking them on a schedule would spend
 * effort on screens nobody is about to lose.
 */

/**
 * Minimum gap between two captures. Well under the two-minute freshness window
 * a snapshot is painted within, so a throttled capture can never be the reason
 * one is too old to use.
 */
export const MIN_CAPTURE_INTERVAL_MS = 1_000;

/** Decide whether a capture should happen now. Pure, so the rule is testable without a page. */
export function shouldCaptureNow(
  lastCaptureAt: number | null,
  now: number,
  minIntervalMs: number = MIN_CAPTURE_INTERVAL_MS,
): boolean {
  if (lastCaptureAt === null) return true;
  if (!Number.isFinite(now) || !Number.isFinite(lastCaptureAt)) return true;
  // A clock that went backwards (system time change) must not lock capture out
  // until it catches up — the snapshot is what protects the person meanwhile.
  if (now < lastCaptureAt) return true;
  return now - lastCaptureAt >= minIntervalMs;
}

export interface CaptureDeps {
  doc: {
    visibilityState?: string;
    addEventListener(type: string, listener: () => void): void;
    removeEventListener(type: string, listener: () => void): void;
  };
  win: {
    addEventListener(type: string, listener: () => void): void;
    removeEventListener(type: string, listener: () => void): void;
  };
  /**
   * Take the snapshot. Passed in rather than imported: WHAT to capture already
   * exists (`captureShellForUpdate`), and this module is only about WHEN. It
   * returns whether anything was actually stored, which the throttle below
   * depends on.
   */
  capture: (now: number) => boolean;
  now: () => number;
  minIntervalMs?: number;
}

/**
 * Wire capture to the moments a page can be left. Returns a teardown function.
 *
 * @returns a function that removes both listeners — so a caller that unmounts
 *   does not leave a capture running against a dead document.
 */
export function installShellSnapshotCapture(deps: CaptureDeps): () => void {
  const { doc, win, capture: takeSnapshot, now, minIntervalMs } = deps;
  let lastCaptureAt: number | null = null;

  const capture = (): void => {
    const at = now();
    if (!shouldCaptureNow(lastCaptureAt, at, minIntervalMs ?? MIN_CAPTURE_INTERVAL_MS)) return;
    // A failed capture does not update the clock: the next opportunity should
    // try again rather than be throttled out on the strength of a write that
    // never happened.
    if (takeSnapshot(at)) lastCaptureAt = at;
  };

  const onPageHide = (): void => capture();
  const onVisibility = (): void => {
    // Only on the way out. Capturing when a tab becomes visible would serialize
    // the screen at the exact moment the person started using it again.
    if (doc.visibilityState === 'hidden') capture();
  };

  win.addEventListener('pagehide', onPageHide);
  doc.addEventListener('visibilitychange', onVisibility);

  return () => {
    win.removeEventListener('pagehide', onPageHide);
    doc.removeEventListener('visibilitychange', onVisibility);
  };
}
